import {useContext, useEffect, useState} from "react";
import {LAKE_CHART_MODES} from "../utils/ChartFormatUtils";
import SensorService from "../API/SensorService";
import {
    getDateByTimestamp,
    getMinusMonthTimestamp,
    getMinusWeekTimestamp,
    getMinusYearTimestamp,
    getTodayEndTimestamp,
    getWeekDayName,
    INTERVALS,
    FIRST_TIMESTAMP,
    formatTooltipDate,
    getEndOfDay,
    formatDayNumberWithMonthName,
    getEachExactDayNumberDate,
    DATE_CONSTANTS,
    isSameDay,
    getDaysGap
} from "../utils/DateUtils";
import {CommonContext} from "../context";

export function useChartData() {
    const {isAuth, setIsSensorInMaintenance} = useContext(CommonContext);
    const [activeMode, setActiveMode] = useState(LAKE_CHART_MODES.week);

    const [weekData, setWeekData] = useState(); //labels, temperatures, waterLevels
    const [monthData, setMonthData] = useState();
    const [yearData, setYearData] = useState();
    const [maxData, setMaxData] = useState();

    const todayTimestamp = getTodayEndTimestamp()

    useEffect(() => {
        if (isAuth) {
            fetchAllData();
        }
    }, [activeMode, isAuth]);

    function fetchAllData() {
        if (!weekData) {
            fetchWeekData();
        }
        if (!monthData) {
            fetchMonthData();
        }
        if (!yearData) {
            fetchYearData();
        }
        if (!maxData) {
            fetchMaxData();
        }
    }

    async function fetchDataBy(startTimestamp, endTimestamp, dailyMeasurementsNumber, callback) {
        const interval = INTERVALS.day / dailyMeasurementsNumber;

        const tempResponse = await SensorService.fetchTemperature(startTimestamp, endTimestamp, interval);
        const temperatures = filterResponse(tempResponse, dailyMeasurementsNumber);

        const waterResponse = await SensorService.fetchWaterLevel(startTimestamp, endTimestamp, interval);
        const waterLevels = filterResponse(waterResponse, dailyMeasurementsNumber);

        callback(temperatures, waterLevels);
    }

    function fetchWeekData() {
        fetchDataBy(getMinusWeekTimestamp(), todayTimestamp, 2, (temperatures, waterLevels) => {
            temperatures.shift(); //removing first element, so first day of chart didn't have 3 values as server returns
            waterLevels.shift();

            const labels = [];
            temperatures.forEach(element => labels.push(toLabelObject(
                labels.length % 2 === 0 ? getWeekDayName(element.timestamp) : null, //there is 2 elements per day
                formatTooltipDate(getDateByTimestamp(element.timestamp))
            )));

            setWeekData(prev => ({
                ...prev,
                labels: labels,
                temperatures: temperatures.map(element => element.value),
                waterLevels: waterLevels.map(element => element.value)
            }));
        });
    }

    function fetchMonthData() {
        const eachNDay = 5;

        const getEachNDayNumber = (date, n) => {
            return date.getDate() % n === 0
                ? formatDayNumberWithMonthName(date)
                : null;
        };

        fetchDataBy(getMinusMonthTimestamp(), todayTimestamp, 1, (temperatures, waterLevels) => {
            defineIsSensorInMaintenance(temperatures);

            const labelsResult = [];
            const temperaturesResult = [];
            const waterLevelsResult = [];
            const iterationDate = findLatestDate(temperatures);
            let dataIterator = temperatures.length - 1;

            for (let i = 0; i < 31; i++) {
                let shouldRealDataBeUsed = dataIterator >= 0 && isSameDay(iterationDate, getDateByTimestamp(temperatures[dataIterator].timestamp));
                
                if (shouldRealDataBeUsed) {
                    const date = getDateByTimestamp(temperatures[dataIterator].timestamp);
                    labelsResult.unshift(toLabelObject(
                        getEachNDayNumber(date, eachNDay),
                        formatTooltipDate(date)
                    ));
                    temperaturesResult.unshift(temperatures[dataIterator].value);
                    waterLevelsResult.unshift(waterLevels[dataIterator].value);

                    dataIterator--;
                } else {
                    labelsResult.unshift(toLabelObject(
                        getEachNDayNumber(iterationDate, eachNDay),
                        ''
                    ));
                    temperaturesResult.unshift(null);
                    waterLevelsResult.unshift(null);
                }

                iterationDate.setDate(iterationDate.getDate() - 1);
            }

            setMonthData(prev => ({
                ...prev,
                labels: labelsResult,
                temperatures: temperaturesResult,
                waterLevels: waterLevelsResult
            }));
        });
    }

    function defineIsSensorInMaintenance(data) {
        if (!data || data.length === 0) {
            setIsSensorInMaintenance(true);
            return;
        }

        const newestTimestamp = data[data.length - 1].timestamp;
        const daysDifference = (todayTimestamp - newestTimestamp) / DATE_CONSTANTS.secondsInDay;
        if (daysDifference > 2) {
            setIsSensorInMaintenance(true);
        }
    }

    function fetchYearData() {
        const minusYearTimestamp = getMinusYearTimestamp();
        const fullYearElementsNumber = 365; //used to fill chart if there is not enough data for last 365 days

        fetchDataBy(minusYearTimestamp, todayTimestamp, 1, (temperatures, waterLevels) => {
            const currentDate = findLatestDate(temperatures);
            const newestDayNumber = Math.min(currentDate.getDate(), 28);

            const labelsResult = [];
            const temperaturesResult = [];
            const waterLevelsResult = [];
            let dataIterator = temperatures.length - 1;

            for (let i = 0; i < fullYearElementsNumber; i++) {
                let shouldRealDataBeUsed = dataIterator >= 0 && isSameDay(currentDate, getDateByTimestamp(temperatures[dataIterator].timestamp));
                
                if (shouldRealDataBeUsed) {
                    const date = getDateByTimestamp(temperatures[dataIterator].timestamp);
                    labelsResult.unshift(toLabelObject(
                        getEachExactDayNumberDate(date, newestDayNumber),
                        formatTooltipDate(date)
                    ));
                    temperaturesResult.unshift(temperatures[dataIterator].value);
                    waterLevelsResult.unshift(waterLevels[dataIterator].value);

                    dataIterator--;
                } else {
                    labelsResult.unshift(toLabelObject(
                        getEachExactDayNumberDate(currentDate, newestDayNumber),
                        ''
                    ));
                    temperaturesResult.unshift(null);
                    waterLevelsResult.unshift(null);
                }

                currentDate.setDate(currentDate.getDate() - 1);
            }

            setYearData(prev => ({
                    ...prev,
                    labels: labelsResult,
                    temperatures: temperaturesResult,
                    waterLevels: waterLevelsResult
            }));
        });
    }

    function fetchMaxData() {
        fetchDataBy(FIRST_TIMESTAMP, todayTimestamp, 1, (temperatures, waterLevels) => {
            const newestDayNumber = findNewestDayNumber(temperatures);

            const lastElementIndex = temperatures.length - 1;
            const labelsResult = [];
            const temperaturesResult = [];
            const waterLevelsResult = [];
            for (let i = 0; i <= lastElementIndex; i++) {
                const date = getDateByTimestamp(temperatures[i].timestamp);

                labelsResult.push(toLabelObject(
                    getEachExactDayNumberDate(date, newestDayNumber),
                    formatTooltipDate(date)
                ));
                temperaturesResult.push(temperatures[i].value);
                waterLevelsResult.push(waterLevels[i].value);

                if (i < lastElementIndex) {
                    const nextElementDate = getDateByTimestamp(temperatures[i + 1].timestamp);
                    const daysGap = getDaysGap(date, nextElementDate);
                    for (let gapIndex = 1; gapIndex <= daysGap; gapIndex++) { //to create chart gap when there's no data for particular days
                        const gapDate = new Date(date);
                        gapDate.setDate(date.getDate() + gapIndex);
                        
                        labelsResult.push(toLabelObject(
                            getEachExactDayNumberDate(gapDate, newestDayNumber),
                            ''
                        ));
                        temperaturesResult.push(null);
                        waterLevelsResult.push(null);
                    }
                }
            }

            setMaxData(prev => ({
                ...prev,
                labels: labelsResult,
                temperatures: temperaturesResult,
                waterLevels: waterLevelsResult
            }));
        });
    }

    // Removes zero-value entries and trims excess entries per day to dailyMeasurementsNumber,
    // keeping the newest. e.g. dailyMeasurementsNumber=1, two entries on 10.06.2026 → oldest dropped.
    function filterResponse(response, dailyMeasurementsNumber) {
        const withoutZeroValues = response.data.filter(element => element.value !== 0);

        const result = [];
        let lastDate = null;
        let sameDayCounter = 0;
        for (let i = withoutZeroValues.length - 1; i >= 0; i--) {
            const date = getDateByTimestamp(withoutZeroValues[i].timestamp);
            if (!lastDate || !isSameDay(lastDate, date)) {
                lastDate = date;
                sameDayCounter = 1;
            } else {
                sameDayCounter++;
            }

            if (sameDayCounter <= dailyMeasurementsNumber) {
                result.unshift(withoutZeroValues[i]);
            }
        }

        return result;
    }

    function toLabelObject(axisValue, tooltipValue) {
        return JSON.stringify({
            axisValue: axisValue,
            tooltipValue: tooltipValue
        })
    }
    
    function findLatestDate(responseData) {
        return responseData.length > 0
            ? getDateByTimestamp(responseData[responseData.length - 1].timestamp)
            : getEndOfDay();
    }

    function findNewestDayNumber(responseData) {
        return responseData.length > 0
            ? getDateByTimestamp(responseData[responseData.length - 1].timestamp).getDate()
            : getEndOfDay().getDate();
    }

    let activeData;
    let activeModeStartTimestamp;
    switch (activeMode) {
        case LAKE_CHART_MODES.week:
            activeData = weekData;
            activeModeStartTimestamp = getMinusWeekTimestamp();
            break;
        case LAKE_CHART_MODES.month:
            activeData = monthData;
            activeModeStartTimestamp = getMinusMonthTimestamp();
            break;
        case LAKE_CHART_MODES.year:
            activeData = yearData;
            activeModeStartTimestamp = getMinusYearTimestamp();
            break;
        case LAKE_CHART_MODES.max:
            activeData = maxData;
            activeModeStartTimestamp = FIRST_TIMESTAMP;
            break;
        default:
            activeData = weekData;
            activeModeStartTimestamp = getMinusWeekTimestamp();
            break;
    }

    function downloadXlsxData() {
        SensorService.downloadXlsxData(activeModeStartTimestamp, todayTimestamp);
    }

    return [activeMode, setActiveMode, activeData, downloadXlsxData];
}